/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo } from 'react';

import { FilterDimension } from 'components/FilterDimension';
import { Dimensions, Metrics } from 'components/Incrementality/incrementalityTypes';

import { useOrganization } from 'modules/organization/useOrganization';
import { IncrementalityFilters } from 'modules/performance/performanceApiTypes';

import { DSP_ADFORM, DSP_FREEWHEEL } from 'utils/constants';

export const IncrementalityFilter = ({
  incrementalityFilter,
  onChange,
}: {
  incrementalityFilter: IncrementalityFilters | null | undefined;
  onChange: (data: IncrementalityFilters) => void;
}) => {
  const { dsp } = useOrganization();
  const dimension = incrementalityFilter?.dimension || Dimensions.OVERALL.value;
  const metric = incrementalityFilter?.metric || Metrics.LEADS.value;

  useEffect(() => {
    // Reset to LEADS if ACTIVITY is selected but DSP is not Adform or FreeWheel
    if (metric === 'ACTIVITY' && dsp !== DSP_ADFORM && dsp !== DSP_FREEWHEEL) {
      onChange({ metric: Metrics.LEADS.value, dimension });
    }
  }, [dsp, metric, dimension]);

  const getOptions = useCallback(
    (items: Record<string, { label: string; value: string }>) =>
      Object.entries(items).map(([key, { label }]) => ({
        label,
        value: key,
      })),
    [],
  );

  const filteredDimensions = useMemo(() => {
    if (dsp === DSP_ADFORM || dsp === DSP_FREEWHEEL) {
      // For Adform/FreeWheel: exclude MEDIA_TYPE
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { MEDIA_TYPE, ...adformDimensions } = Dimensions;

      return adformDimensions;
    }
    // For other DSPs: exclude DEVICE_TYPE
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { DEVICE_TYPE, ...otherDimensions } = Dimensions;

    return otherDimensions;
  }, [dsp]);

  const filteredMetrics = useMemo(() => {
    if (dsp === DSP_ADFORM || dsp === DSP_FREEWHEEL) {
      // For Adform/FreeWheel: show all metrics including ACTIVITY
      return Metrics;
    }
    // For other DSPs: exclude ACTIVITY
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ACTIVITY, ...otherMetrics } = Metrics;

    return otherMetrics;
  }, [dsp]);

  return (
    <>
      <FilterDimension
        prefixText="Dimension:"
        allOptions={getOptions(filteredDimensions)}
        selectedOption={dimension}
        onChange={(dimension) => {
          onChange({ metric, dimension });
        }}
      />
      <FilterDimension
        prefixText="Metric:"
        allOptions={getOptions(filteredMetrics)}
        selectedOption={metric}
        onChange={(metric) => {
          onChange({ metric, dimension });
        }}
      />
    </>
  );
};
